'use client';

import { useTranslations } from 'next-intl';
import { Info, Route, Sparkles } from 'lucide-react';

import type {
  RunnerDispatchPolicy,
  RunnerDispatchPolicyEntityType,
  RunnerDispatchPolicyRecommendation,
} from '../lib/api';
import { resolveEntityReferenceLabel } from '../lib/entityReferencePrompt';
import { noRunnerDispatchPolicyValue } from '../lib/runnerDispatchPolicySelection';
import { EntityStatusBadge } from './EntityStatusBadge';
import { Select, type SelectOption } from './Select';

type DaemonTriggerDispatchPolicyPickerState = {
  selectedDispatchPolicyId: string | null;
  dispatchPolicies: RunnerDispatchPolicy[];
  dispatchPolicyRecommendation?: RunnerDispatchPolicyRecommendation;
  dispatchContext?: {
    entityType: RunnerDispatchPolicyEntityType;
    entityId: string;
    entityState: string;
    entityLabel?: string;
  };
};

type DaemonTriggerDispatchPolicyPickerActions = {
  onDispatchPolicyChange: (value: string | null) => void;
  onRestoreRecommendedPolicy?: () => void;
};

type DaemonTriggerDispatchPolicyPickerProps = {
  state: DaemonTriggerDispatchPolicyPickerState;
  actions: DaemonTriggerDispatchPolicyPickerActions;
};

export const DaemonTriggerDispatchPolicyPicker = ({ state, actions }: DaemonTriggerDispatchPolicyPickerProps) => {
  const tLayout = useTranslations('components.appLayout');
  const recommendedPolicyId = state.dispatchPolicyRecommendation?.policyId ?? null;
  // 정책이 실제로 걸린 요청에서만 대상을 노출한다. 정책이 붙는 순간 모달이 프롬프트의 엔티티
  // 참조 칩을 제거하고 대상을 dispatchPolicy 페이로드로만 넘기기 때문에, 이 줄이 없으면
  // 사용자가 무엇에 대해 요청하는지 확인할 방법이 사라진다. "사용 안 함"에서는 칩이 되살아나고
  // 사용자가 그 칩을 지울 수도 있어서, 계속 띄우면 프롬프트와 어긋난다.
  const dispatchTarget =
    state.dispatchContext &&
    state.selectedDispatchPolicyId !== null &&
    state.selectedDispatchPolicyId !== noRunnerDispatchPolicyValue &&
    state.dispatchPolicies.some((policy) => policy.id === state.selectedDispatchPolicyId)
      ? state.dispatchContext
      : null;
  const policyOptions: SelectOption[] = [
    ...state.dispatchPolicies.map((policy) => ({
      value: policy.id,
      label: policy.promptPreset.name,
      leadingIcon: policy.id === recommendedPolicyId ? <Sparkles className="h-3.5 w-3.5 text-primary" /> : undefined,
    })),
    { value: noRunnerDispatchPolicyValue, label: tLayout('runnerDispatchPolicyDisabled') },
  ];

  return (
    <div className="rounded-lg border-2 border-primary/40 bg-base-200/30 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <Route className="h-4 w-4 shrink-0 text-primary" />
        <span className="text-xs font-medium text-base-content">{tLayout('runnerDispatchPolicyTitle')}</span>
        {recommendedPolicyId && state.selectedDispatchPolicyId === recommendedPolicyId ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
            {tLayout('runnerDispatchPolicyRecommended')}
          </span>
        ) : null}
      </div>
      {dispatchTarget ? (
        <div className="mt-2 flex min-w-0 items-center gap-1.5">
          <span className="shrink-0 text-[11px] font-medium text-[var(--color-fg-subtle)]">
            {tLayout('runnerDispatchPolicyTargetLabel')}
          </span>
          <span className="shrink-0 rounded-full bg-[var(--chip-neutral-bg)] px-1.5 py-0.5 text-[11px] text-[var(--chip-neutral-fg)]">
            {tLayout(
              `runnerDispatchPolicyTargetEntityTypes.${dispatchTarget.entityType}` as Parameters<typeof tLayout>[0],
            )}
          </span>
          <EntityStatusBadge entityType={dispatchTarget.entityType} status={dispatchTarget.entityState} size="xxs" />
          <span className="truncate text-xs text-base-content">
            {resolveEntityReferenceLabel({
              entityType: dispatchTarget.entityType,
              entityId: dispatchTarget.entityId,
              entityTypeLabel: tLayout(
                `runnerDispatchPolicyTargetEntityTypes.${dispatchTarget.entityType}` as Parameters<typeof tLayout>[0],
              ),
              labelCandidates: [dispatchTarget.entityLabel],
            })}
          </span>
        </div>
      ) : null}
      <Select
        options={policyOptions}
        value={state.selectedDispatchPolicyId ?? ''}
        onChange={(value) => actions.onDispatchPolicyChange(value || null)}
        className="mt-2 min-w-0"
        fullWidth
      />
      {state.dispatchPolicyRecommendation ? (
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1 text-xs text-[var(--color-fg-muted)]">
            <Info className="h-3.5 w-3.5 shrink-0 text-primary/70" />
            <span className="truncate">
              {tLayout(
                `runnerDispatchPolicyRecommendReason.${state.dispatchPolicyRecommendation.reason}` as Parameters<
                  typeof tLayout
                >[0],
              )}
            </span>
          </span>
          {state.selectedDispatchPolicyId !== recommendedPolicyId && actions.onRestoreRecommendedPolicy ? (
            <button
              type="button"
              className="btn btn-ghost btn-xs shrink-0 text-primary"
              onClick={actions.onRestoreRecommendedPolicy}
            >
              {tLayout('runnerDispatchPolicyRestoreRecommended')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export type {
  DaemonTriggerDispatchPolicyPickerActions,
  DaemonTriggerDispatchPolicyPickerProps,
  DaemonTriggerDispatchPolicyPickerState,
};
